import { StrictMode, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
// Primär-Route eager (sofortiges Hauptfenster, kein Lade-Flash):
import { InboxPage } from './pages/InboxPage';
import { ConversationPage } from './pages/ConversationPage';
// Sekundär-Seiten lazy (Route-Code-Splitting) — recharts (ReportsPage) u.a. verlassen
// damit das Haupt-Bundle und werden erst beim Aufruf der Seite geladen.
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })));
const RulesPage = lazy(() => import('./pages/RulesPage').then((m) => ({ default: m.RulesPage })));
const ContactsPage = lazy(() => import('./pages/ContactsPage').then((m) => ({ default: m.ContactsPage })));
const ReportsPage = lazy(() => import('./pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })));
const BroadcastsPage = lazy(() => import('./pages/BroadcastsPage').then((m) => ({ default: m.BroadcastsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    // 60s statt 30s: weniger Refetches bei Navigation; selten ändernde Daten
    // (Channel-Accounts/Templates) setzen bei Bedarf gezielt höhere Werte.
    queries: { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('root element missing');

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<App />}>
              <Route index element={<Navigate to="/inbox" replace />} />
              <Route path="inbox" element={<InboxPage />}>
                <Route path=":conversationId" element={<ConversationPage />} />
              </Route>
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="templates" element={<TemplatesPage />} />
              <Route path="rules" element={<RulesPage />} />
              <Route path="broadcasts" element={<BroadcastsPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="audit" element={<AuditPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);

// PWA: Service-Worker registrieren (App-Shell-Offline + Web-Push teilen sich /sw.js).
// Idempotent — eine erneute Registrierung derselben URL (z. B. zusätzlich in SettingsPage
// für Push) ist unkritisch. Defensiv: nur wenn der Browser Service-Worker unterstützt.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* nicht-fatal: App funktioniert auch ohne registrierten Service-Worker */
    });
  });
}
